import { Box, Typography, makeStyles } from '@material-ui/core';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { OptionType, SGSButton, SGSFormInput, SGSSelectCtr } from 'components';
import SGSInputCurrency from 'components/SGSCurrency';
import { optionReasonForWorkStop } from 'features/arretTravail/models/workStopReason';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { SalaryNotFulTime } from '.';

type TableSalaireProps = {
    salaries: SalaryNotFulTime[];
    appendSalary: () => void;
    removeSalary: (index: number) => void;
    periodsOptions: OptionType[];
    store: any;
};

export const TableSalaryInterruptionPeriodS3201: React.FC<TableSalaireProps> = ({ salaries, appendSalary, removeSalary, periodsOptions, store }) => {
    const classes = useStyles();
    const { setValue, getValues, trigger, control } = useFormContext();
    const [canAddMore, setCanAddMore] = useState(false);
    const watchedSalaires = useWatch({ name: 'interruptionActivityS3201', control });

    useEffect(() => {
        if (!store?.interruptionActivityS3201?.length) return;

        store.interruptionActivityS3201.forEach((s, index) => {
            const filteredSalaryBaseById = store.salaryBaseS3201.find((o) => o.id === s.period);

            if (filteredSalaryBaseById) {
                const currentAmount = getValues(`interruptionActivityS3201.${index}.amount`);
                if (currentAmount !== filteredSalaryBaseById.amount) {
                    setValue(`interruptionActivityS3201.${index}.amount`, filteredSalaryBaseById.amount);
                }
            }
        });
    }, [store, setValue, getValues]);

    useEffect(() => {
        const selectedPeriods = watchedSalaires.map((s) => s.period);
        const remainingOptions = periodsOptions.some((option) => !selectedPeriods.includes(option.value));
        const allFieldsHaveValues = watchedSalaires.every((item) => Object.keys(item).every((key) => key === 'id' || (item[key] !== null && item[key] !== '')));
        setCanAddMore(remainingOptions && allFieldsHaveValues);
    }, [watchedSalaires, periodsOptions]);

    const handlePeriod = (event) => {
        const periodPath = event.target.name;
        const periodIndex = event.target.value;
        const salaryBase = store.salaryBaseS3201.find((o) => o.id === periodIndex);

        if (!salaryBase) {
            console.error(`No salary base found for index: ${periodIndex}`);
            return;
        }

        setValue(periodPath, periodIndex);
        const amountPath = periodPath.replace('period', 'amount');
        setValue(amountPath, salaryBase.amount);
    };

    const handleAdd = async () => {
        const isValid = await trigger('interruptionActivityS3201');
        if (isValid) appendSalary();
    };

    const getFilteredPeriodsOptions = (index) => {
        const selectedPeriods = salaries.map((s) => s.period);
        return periodsOptions.filter((option) => !selectedPeriods.includes(option.value) || salaries[index]?.period === option.value);
    };

    return (
        <TContainer>
            <Table>
                <TableHead>
                    <TRow>
                        <THeaderBlue colSpan={3} align="center">
                            <Typography>SALAIRE DE BASE</Typography>
                        </THeaderBlue>
                        <THeaderBlue colSpan={4} rowSpan={2} align="center">
                            <Typography>L'ASSURE N'A PAS TRAVAILLE A TEMPS COMPLET PENDANT LA PERIODE DE REFERENCE</Typography>
                        </THeaderBlue>
                        <THeaderBlue rowSpan={2}></THeaderBlue>
                    </TRow>
                    <TableRow className={classes.tableHeader}>
                        <THeader colSpan={2}>
                            <Typography className={classes.txtGreen}>PERIODE DE REFERENCE</Typography>
                        </THeader>
                        <THeader rowSpan={2}>
                            <Typography className={classes.txtGreen}>Montant du salaire</Typography>
                        </THeader>
                    </TableRow>
                    <TableRow className={classes.tableHeader}>
                        <THeader>
                            <Typography className={classes.txtGreen}>Du</Typography>
                        </THeader>
                        <THeader>
                            <Typography className={classes.txtGreen}>Au</Typography>
                        </THeader>
                        <THeader>
                            <Typography className={classes.txtGreen}>Motif</Typography>
                        </THeader>
                        <THeader>
                            <Typography className={classes.txtGreen}>H réelles</Typography>
                        </THeader>
                        <THeader>
                            <Typography className={classes.txtGreen}>H complet</Typography>
                        </THeader>
                        <THeader>
                            <Typography className={classes.txtGreen}>Salaire rétabli</Typography>
                        </THeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {salaries.map((item, index) => (
                        <TableRow className="detail" key={item.id}>
                            <TCell colSpan={2} width={280}>
                                <SGSSelectCtr fullWidth name={`interruptionActivityS3201.${index}.period`} options={getFilteredPeriodsOptions(index)} variant="outlined" onChange={handlePeriod} />
                            </TCell>
                            <TCell>
                                <SGSFormInput variant="outlined" name={`interruptionActivityS3201.${index}.amount`} disabled />
                            </TCell>
                            <TCell width={280}>
                                <SGSSelectCtr fullWidth name={`interruptionActivityS3201.${index}.reason`} options={optionReasonForWorkStop} variant="outlined" />
                            </TCell>
                            <TCell>
                                <SGSFormInput variant="outlined" name={`interruptionActivityS3201.${index}.realH`} />
                            </TCell>
                            <TCell>
                                <SGSFormInput variant="outlined" name={`interruptionActivityS3201.${index}.completH`} />
                            </TCell>
                            <TCell>
                                <SGSInputCurrency name={`interruptionActivityS3201.${index}.restoredSalary`} />
                            </TCell>
                            <TCell>
                                <Box style={{ width: '50px' }}>
                                    <SGSButton type="button" onClick={() => removeSalary(index)}>
                                        -
                                    </SGSButton>
                                </Box>
                            </TCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Box textAlign="center" className="mt-2">
                <SGSButton type="button" onClick={handleAdd} disabled={!canAddMore}>
                    +
                </SGSButton>
            </Box>
        </TContainer>
    );
};

const useStyles = makeStyles(() => ({
    header: {
        height: '54px',
    },
    dtEmis: {
        color: '#fff',
        fontSize: '0.8rem',
        fontFamily: 'Open Sans',
        height: '100%',
        width: '170px',
    },
    txtGreen: {
        color: '#2574A9',
        fontSize: '0.93rem',
        fontFamily: 'Open Sans',
        fontWeight: 700,
    },
    tableHeader: {},
}));

const TContainer = styled(TableContainer)(() => ({
    boxShadow: 'none',
    borderTop: 'none',
}));

const TRow = styled(TableRow)(() => ({
    borderBottom: 'none',
    fontFamily: 'Open Sans',
}));

const THeaderBlue = styled(TableCell)(() => ({
    backgroundColor: '#3399DB',
    color: '#FFF',
    fontFamily: 'Open Sans',
    fontSize: '0.85rem',
    textAlign: 'center',
    paddingTop: '5px',
    height: '54px',
    paddingBottom: '3px',
    border: '1px solid #3399DB',
    '&:last-child': {
        background: 'none',
        border: 'none',
    },
}));

const THeader = styled(TableCell)(() => ({
    fontFamily: 'Open Sans',
    fontSize: '0.85rem',
    textAlign: 'center',
    borderLeft: '1px solid #cdcdcd',
    borderRight: '1px solid #cdcdcd',
    borderBottom: '1px solid #cdcdcd',
    borderTop: 'none',
    background: '#F5F5F5',
}));

const TCell = styled(TableCell)(() => ({
    fontFamily: 'Open Sans',
    fontSize: '0.85rem',
    textAlign: 'center',
    verticalAlign: 'top',
    borderLeft: '1px solid #cdcdcd',
    borderRight: '1px solid #cdcdcd',
    borderBottom: '1px solid #cdcdcd',
    borderTop: 'none',
    background: '#FFF',
    '&:last-child': {
        background: 'none',
        border: 'none',
    },
}));
